import {
  boardCopyBodySchema,
  boardCopyResultSchema,
  cardCopyBodySchema,
  cardCopyResultSchema,
  listCopyBodySchema,
  listCopyResultSchema,
  type Board,
  type BoardCopyBody,
  type BoardCopyResult,
  type BoardContents,
  type BoardFaceList,
  type CardCopyBody,
  type CardCopyResult,
  type CardDetail,
  type List,
  type ListCopyBody,
  type ListCopyResult,
} from '@molt/shared';
import {
  useMutation,
  useQueryClient,
  type UseMutationResult,
} from '@tanstack/react-query';
import { announce } from './announce';
import { post } from './api';
import { setBoardContentsQueries } from './cross-board-move';
import { customFieldBadgesFromCardDetail } from './custom-fields';
import { queryKeys } from './query-keys';

type CopyFailureTranslate = (
  key: string,
  values?: Record<string, string | number>,
  count?: number,
) => string;

export interface CopyCardVariables {
  readonly sourceBoardId: string;
  readonly sourceCardId: string;
  readonly sourceTitle: string;
  readonly destinationBoardId: string;
  readonly destinationListId: string;
  readonly previousCardId: string | null;
  readonly nextCardId: string | null;
  readonly title: string;
  readonly keepChecklists: boolean;
  readonly keepLabels: boolean;
  readonly keepMembers: boolean;
  readonly keepAttachments: boolean;
  readonly keepDates: boolean;
}

export interface CopyListVariables {
  readonly sourceBoardId: string;
  readonly sourceListId: string;
  readonly sourceTitle: string;
  readonly destinationBoardId: string;
  readonly previousListId: string | null;
  readonly nextListId: string | null;
  readonly title?: string | undefined;
}

export interface CopyBoardVariables {
  readonly sourceBoardId: string;
  readonly sourceTitle: string;
  readonly workspaceId: string;
  readonly visibility: 'private' | 'workspace_visible' | 'public';
  readonly keepCards: boolean;
  readonly title?: string | undefined;
}

export function copyCard(cardId: string, body: CardCopyBody): Promise<CardCopyResult> {
  return post(
    `/cards/${encodeURIComponent(cardId)}/copy`,
    cardCopyResultSchema,
    cardCopyBodySchema.parse(body),
  );
}

export function copyList(listId: string, body: ListCopyBody): Promise<ListCopyResult> {
  return post(
    `/lists/${encodeURIComponent(listId)}/copy`,
    listCopyResultSchema,
    listCopyBodySchema.parse(body),
  );
}

export function copyBoard(
  boardId: string,
  body: BoardCopyBody,
): Promise<BoardCopyResult> {
  return post(
    `/boards/${encodeURIComponent(boardId)}/copy`,
    boardCopyResultSchema,
    boardCopyBodySchema.parse(body),
  );
}

export function applyCopiedCardToBoardContents(
  previous: BoardContents | undefined,
  detail: CardDetail,
  previousCardId: string | null,
  nextCardId: string | null,
): BoardContents | undefined {
  if (previous === undefined) return undefined;

  const listIndex = previous.lists.findIndex((list) => list.id === detail.listId);
  if (listIndex === -1) return previous;

  const targetList = previous.lists[listIndex]!;
  if (targetList.cards.some((card) => card.id === detail.id)) return previous;
  const copied = cardDetailToBoardFaceCard(detail);
  const cards = insertAtNeighbours(targetList.cards, copied, previousCardId, nextCardId);

  return {
    ...previous,
    lists: previous.lists.map((list, index) =>
      index === listIndex
        ? {
            ...list,
            cards,
            cardCount: list.cardCount + 1,
            openCardCount: list.openCardCount + 1,
          }
        : list,
    ),
  };
}

export function applyCopiedListToBoardContents(
  previous: BoardContents | undefined,
  list: List,
  previousListId: string | null,
  nextListId: string | null,
): BoardContents | undefined {
  if (previous === undefined) return undefined;
  if (previous.board.id !== list.boardId) return previous;
  if (previous.lists.some((entry) => entry.id === list.id)) return previous;

  return {
    ...previous,
    lists: insertAtNeighbours(
      previous.lists,
      listToEmptyBoardFaceList(list),
      previousListId,
      nextListId,
    ),
  };
}

export function useCopyCard(
  sourceBoardId: string,
  t: CopyFailureTranslate,
): UseMutationResult<CardCopyResult, unknown, CopyCardVariables> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (variables: CopyCardVariables) =>
      copyCard(variables.sourceCardId, {
        listId: variables.destinationListId,
        previousCardId: variables.previousCardId,
        nextCardId: variables.nextCardId,
        title: variables.title,
        keepChecklists: variables.keepChecklists,
        keepLabels: variables.keepLabels,
        keepMembers: variables.keepMembers,
        keepAttachments: variables.keepAttachments,
        keepDates: variables.keepDates,
      }),
    onSuccess: (result, variables) => {
      queryClient.setQueryData(queryKeys.cards.detail(result.card.id), result.card);
      if (variables.destinationBoardId === sourceBoardId) {
        setBoardContentsQueries(queryClient, variables.destinationBoardId, (previous) =>
          applyCopiedCardToBoardContents(
            previous,
            result.card,
            variables.previousCardId,
            variables.nextCardId,
          ),
        );
      }

      void queryClient.invalidateQueries({
        queryKey: queryKeys.boards.contents(sourceBoardId),
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.boards.contents(variables.destinationBoardId),
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.boards.lists(variables.destinationBoardId),
      });
      if (variables.destinationBoardId !== sourceBoardId) {
        void queryClient.invalidateQueries({
          queryKey: queryKeys.boards.labels(variables.destinationBoardId),
        });
      }
      void queryClient.invalidateQueries({
        queryKey: queryKeys.cards.detail(result.card.id),
      });
    },
    onError: (_error, variables) => {
      announce(t('copyMove.card.copyFailed', { title: variables.sourceTitle }));
    },
  });
}

export function useCopyList(
  sourceBoardId: string,
  t: CopyFailureTranslate,
): UseMutationResult<ListCopyResult, unknown, CopyListVariables> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (variables: CopyListVariables) =>
      copyList(variables.sourceListId, {
        ...(variables.destinationBoardId === sourceBoardId
          ? {}
          : { boardId: variables.destinationBoardId }),
        previousListId: variables.previousListId,
        nextListId: variables.nextListId,
        ...(variables.title === undefined ? {} : { title: variables.title }),
      }),
    onSuccess: (result, variables) => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.boards.contents(sourceBoardId),
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.boards.contents(variables.destinationBoardId),
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.boards.lists(sourceBoardId),
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeys.boards.lists(variables.destinationBoardId),
      });
      if (variables.destinationBoardId !== sourceBoardId) {
        void queryClient.invalidateQueries({
          queryKey: queryKeys.boards.labels(variables.destinationBoardId),
        });
        void queryClient.invalidateQueries({
          queryKey: queryKeys.boards.members(variables.destinationBoardId),
        });
      }
    },
    onError: (_error, variables) => {
      announce(t('copyMove.list.copyFailed', { title: variables.sourceTitle }));
    },
  });
}

export function useCopyBoard(
  t: CopyFailureTranslate,
): UseMutationResult<BoardCopyResult, unknown, CopyBoardVariables> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (variables: CopyBoardVariables) =>
      copyBoard(variables.sourceBoardId, {
        workspaceId: variables.workspaceId,
        visibility: variables.visibility,
        keepCards: variables.keepCards,
        ...(variables.title === undefined ? {} : { title: variables.title }),
      }),
    onSuccess: (result) => {
      queryClient.setQueryData(queryKeys.boards.detail(result.board.id), result.board);
      queryClient.setQueriesData<Board[]>(
        { queryKey: queryKeys.workspaces.boards(result.board.workspaceId, 'open') },
        (previous) => appendBoardIfMissing(previous, result.board),
      );
      void queryClient.invalidateQueries({ queryKey: queryKeys.boards.pages() });
      void queryClient.invalidateQueries({
        queryKey: ['workspaces', result.board.workspaceId, 'boards'],
      });
    },
    onError: (_error, variables) => {
      announce(t('copyMove.board.copyFailed', { title: variables.sourceTitle }));
    },
  });
}

function insertAtNeighbours<T extends { id: string }>(
  cards: readonly T[],
  card: T,
  previousCardId: string | null,
  nextCardId: string | null,
): T[] {
  if (nextCardId !== null) {
    const index = cards.findIndex((entry) => entry.id === nextCardId);
    if (index === -1) return [...cards, card];
    return [...cards.slice(0, index), card, ...cards.slice(index)];
  }

  if (previousCardId !== null) {
    const index = cards.findIndex((entry) => entry.id === previousCardId);
    if (index === -1) return [...cards, card];
    return [...cards.slice(0, index + 1), card, ...cards.slice(index + 1)];
  }

  return [...cards, card];
}

function listToEmptyBoardFaceList(list: List): BoardFaceList {
  return {
    ...list,
    openCardCount: 0,
    cards: [],
  };
}

function appendBoardIfMissing(
  previous: Board[] | undefined,
  board: Board,
): Board[] | undefined {
  if (previous === undefined) return undefined;
  if (previous.some((entry) => entry.id === board.id)) return previous;
  return [...previous, board];
}

function cardDetailToBoardFaceCard(detail: CardDetail): BoardFaceList['cards'][number] {
  const checklistItemCount = detail.checklists.reduce(
    (total, checklist) => total + checklist.items.length,
    0,
  );
  const checklistCheckedItemCount = detail.checklists.reduce(
    (total, checklist) => total + checklist.items.filter((item) => item.checked).length,
    0,
  );

  return {
    approvals: null,
    id: detail.id,
    boardId: detail.boardId,
    listId: detail.listId,
    title: detail.title,
    position: detail.position,
    shortLink: detail.shortLink,
    cardNumber: detail.cardNumber,
    ref: detail.ref,
    path: detail.path,
    archived: detail.archived,
    createdAt: detail.createdAt,
    updatedAt: detail.updatedAt,
    agingExempt: detail.agingExempt,
    lastActivityAt: detail.lastActivityAt,
    agingStage: null,
    labels: detail.labels,
    startAt: detail.startAt,
    dueAt: detail.dueAt,
    dueAllDay: detail.dueAllDay,
    completed: detail.completed,
    hasDescription: detail.description !== null,
    commentCount: 0,
    members: detail.members,
    cover: detail.cover,
    checklistItemCount,
    checklistCheckedItemCount,
    attachmentCount: detail.attachments.length,
    /**
     * ⚠ **Derived from the payload, and it was not always.** This projection
     * used to hardcode `[]`, which was true of every card that could reach it
     * while `POST /cards/:id/create-from-template` carried no custom field
     * values. It carries them now — `FR-TMPL-010` AC4 and `FR-CARD-031` AC2,
     * closed in `apps/server/src/copy/copy-card.ts`'s `copyCustomFieldValues`
     * via `CARD_TEMPLATE_ELEMENTS.keepCustomFields` — so the hardcoded `[]`
     * became a projection that **disagreed with the very response it was
     * projecting**: the optimistic patch painted the new card badge-less and
     * the invalidation that follows immediately repainted it with its badges.
     * A transient wrong render rather than a permanently missing badge, but
     * every other field in this function reads `detail`, and this one is now
     * the only one that would have been asserting a fact instead of reading
     * one.
     *
     * ⚠ **The filter is delegated, never restated here.** "Not hidden, shown
     * on front, and has a value" is the card-front contract and
     * `customFieldBadgesFromCardDetail` owns it; inlining those three
     * conditions would make this file a second producer of one contract — the
     * failure this chunk's audit argues against twice — and the two copies
     * would drift the first time `FR-CARD-027`'s show-on-front rule moves.
     *
     * ⚠ **`POST /cards/:id/copy` is unaffected, by derivation rather than by
     * a branch.** `FR-TMPL-013` settles that dialog at five toggles and
     * excludes custom fields permanently (`cardCopyElements()` hardcodes
     * `keepCustomFields: false`), so a copied card's `customFields` carry no
     * values and the helper returns `[]` for it — the same answer the
     * hardcoded literal gave, now for a reason the code can state. Pinned by
     * this file's test rather than left to be rediscovered.
     */
    customFieldBadges: customFieldBadgesFromCardDetail(detail),
    /**
     * The other three literals in this function — `agingStage: null`,
     * `commentCount: 0` and this one — are NOT stale in the way
     * `customFieldBadges` was, and are left alone deliberately so the next
     * reader does not read the omission as an oversight: comments are never
     * copied on any surface, a card created a millisecond ago is in no aging
     * stage the server would report, and no copy tier carries votes.
     */
    voteCount: null,
    /**
     * ⚠ **Derived from the detail, not stubbed at `[]`** — and this is the
     * one-shape guarantee earning its keep. `cardDetailSchema.stickers` and
     * `boardFaceCardSchema.stickers` are the same `placedStickerSchema`, so
     * the card back's answer is directly usable as the card front's, and the
     * two surfaces cannot disagree about a placement (`FR-CARD-032` AC4).
     *
     * Filtered to `'front'`: `ck_card_stickers_surface` admits `front` and
     * `back`, the card back carries both, and a back sticker is not
     * card-front information. `cardDetailSchema.stickers` arrives ordered
     * `surface, z_index, id`, so the filter leaves the stacking order intact
     * without a re-sort.
     */
    stickers: detail.stickers.filter((sticker) => sticker.surface === 'front'),
    /**
     * `false` — a copy is a brand-new card that its maker has not joined, and
     * auto-watch derives from `member.added` and the comment events rather
     * than from a create, so the server answers `false` too. Not `null`:
     * `null` means "there is no *you*" (an anonymous reader), and a copy
     * always has an actor. `lib/cards.ts`'s `placeholderCard` makes the same
     * call for the same reason.
     */
    watching: false,
  };
}
